// Production by default, overridable for local work with REACT_APP_API_URL — the
// same pattern the admin and partner portals already use. Keeping the real API as
// the fallback means a build that forgets to set anything is still correct.
export const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.flatloop.in';
export const S3_BASE_URL = 'https://s3.ap-south-1.amazonaws.com/flatloop-media/';

class ApiClient {
    static async request(endpoint, options = {}) {
        const { method = 'GET', body, token, params } = options;

        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        // Build query string if params are provided
        let url = `${BASE_URL}${endpoint}`;
        if (params) {
            const query = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) query.append(key, value);
            });
            const queryString = query.toString();
            if (queryString) url += `?${queryString}`;
        }

        const config = { method, headers };
        if (body) config.body = JSON.stringify(body);

        const response = await fetch(url, config);

        if (!response.ok) {
            let errorData;
            try { errorData = await response.json(); } catch (e) { }
            const error = new Error(errorData?.message || `API Error: ${response.status}`);
            // Callers need to tell "you must subscribe" (402) apart from a real
            // failure so they can open the Prime sheet instead of showing an error.
            error.status = response.status;
            error.code = errorData?.error;
            error.paymentRequired = response.status === 402;
            throw error;
        }

        const text = await response.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            return text; // Return raw text if not JSON (e.g. presigned URLs)
        }
    }

    static get(endpoint, params = null, token = null) {
        return this.request(endpoint, { method: 'GET', params, token });
    }

    static post(endpoint, body, token = null) {
        return this.request(endpoint, { method: 'POST', body, token });
    }

    static put(endpoint, body, token = null) {
        return this.request(endpoint, { method: 'PUT', body, token });
    }

    static patch(endpoint, body, token = null) {
        return this.request(endpoint, { method: 'PATCH', body, token });
    }

    static delete(endpoint, token = null) {
        return this.request(endpoint, { method: 'DELETE', token });
    }
}

// === AUTHENTICATION DOMAIN ===
export const authApi = {
    register: (countryCode, phoneNumber) => ApiClient.post('/auth/register', { countryCode, phoneNumber }),
    verifyOtp: (countryCode, phoneNumber, otp) => ApiClient.post('/auth/verify-otp', { countryCode, phoneNumber, otp }),
    refresh: (refreshToken) => ApiClient.post('/auth/refresh', { refreshToken }),
    logout: (refreshToken, token) => ApiClient.post('/auth/logout', { refreshToken }, token),
    deleteMe: (token) => ApiClient.delete('/auth/deleteMe', token),
};

// === PUBLIC SEARCH DOMAIN ===
export const publicSearchApi = {
    searchListings: (params) => ApiClient.get('/public/listings/search', params), // params: { lat, lng, radiusKm, rentMin, rentMax, postType, gender, furnishing, page, size }
    // Detail endpoints require auth and decide contact visibility from the caller's
    // subscription, so the token has to go with them.
    getListingDetail: (id, token) => ApiClient.get(`/public/listings/${id}`, null, token),
    searchRequirements: (params) => ApiClient.get('/public/requirements/search', params),
    getRequirementDetail: (id, params, token) => ApiClient.get(`/public/requirements/${id}`, params, token), // params: { lat, lng }
};


// === USER INFO DOMAIN ===
export const userApi = {
    getInfo: (token) => ApiClient.get('/user-info', null, token),
    createEntity: (userInfoRequest, token) => ApiClient.post('/user-info', userInfoRequest, token),
    updateEntity: (userInfoRequest, token) => ApiClient.put('/user-info', userInfoRequest, token),
};

// === FLAT MANAGEMENT DOMAIN ===
export const flatApi = {
    // Requirements
    getRequirements: (token) => ApiClient.get('/flat/requirements', null, token),
    postRequirement: (reqDto, token) => ApiClient.post('/flat/requirements', reqDto, token),
    updateRequirement: (reqDto, token) => ApiClient.patch('/flat/requirements', reqDto, token),
    deleteRequirement: (token) => ApiClient.delete('/flat/requirements', token),

    // Listings
    getListings: (token) => ApiClient.get('/flat/listings', null, token),
    postListing: (listingDto, token) => ApiClient.post('/flat/listings', listingDto, token),
    updateListing: (listingDto, token) => ApiClient.patch('/flat/listings', listingDto, token),
    getListingById: (id, token) => ApiClient.get(`/flat/listings/${id}`, null, token),
    deleteListing: (id, token) => ApiClient.delete(`/flat/listings/${id}`, token),
};

// === CHAT DOMAIN ===
export const chatApi = {
    getRooms: (token) => ApiClient.get('/chat/rooms', null, token),
    createOrGetRoom: (chatRoomReq, token) => ApiClient.post('/chat/rooms', chatRoomReq, token),
    sendMessage: (sendMsgReq, token) => ApiClient.post('/chat/messages', sendMsgReq, token),
    getUnreadCount: (token) => ApiClient.get('/chat/unread-count', null, token),
    getMessages: (roomId, params, token) => ApiClient.get(`/chat/rooms/${roomId}/messages`, params, token), // params: { page, size }
};

// === FILE DOMAIN ===
export const fileApi = {
    getPresignedUrl: (fileName, contentType, token) => ApiClient.get('/api/files/presigned-url', { fileName, contentType }, token),

    /**
     * Upload a file to S3 via presigned URL.
     * Handles browser CORS by proxying through the backend if direct S3 upload fails.
     * @param {File} file - The browser File object to upload
     * @param {string} token - Auth token
     * @param {{watermark?: boolean}} [options] - watermark routes the upload through
     *        the API so it comes back stamped with the flatloop.in wordmark
     * @returns {Promise<{key: string, url: string}>} S3 key and public URL
     */
    uploadFile: async (file, token, options = {}) => {
        if (options.watermark) {
            return fileApi.uploadWatermarked(file, token);
        }

        // Step 1: Get presigned URL from backend
        const presigned = await ApiClient.get('/api/files/presigned-url', {
            fileName: file.name,
            contentType: file.type,
        }, token);

        const { url: presignedUrl, key } = presigned;

        // Step 2: Upload file to S3 using presigned URL
        // Try direct S3 upload first
        try {
            const s3Response = await fetch(presignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
            });

            if (!s3Response.ok) {
                throw new Error(`S3 upload failed: ${s3Response.status}`);
            }
        } catch (err) {
            // If CORS or network error, try uploading via backend proxy
            console.warn('[FileUpload] Direct S3 upload failed (likely CORS), trying backend proxy...', err.message);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('presignedUrl', presignedUrl);
            formData.append('contentType', file.type);

            const proxyResponse = await fetch(`${BASE_URL}/api/files/proxy-upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Do NOT set Content-Type - browser sets it with boundary for FormData
                },
                body: formData,
            });

            if (!proxyResponse.ok) {
                // If proxy also fails, the S3 CORS must be configured
                throw new Error(
                    'Image upload failed. The S3 bucket needs CORS configuration. ' +
                    'Please add AllowedOrigins: ["http://localhost:3000"] to your S3 bucket CORS settings.'
                );
            }
        }

        const publicUrl = `${S3_BASE_URL}${key}`;
        return { key, url: publicUrl };
    },

    /**
     * Upload through the API, which stamps the flatloop.in wordmark before the
     * image reaches S3. Used for listing photos; profile pictures keep the
     * direct presigned path so they stay unbranded.
     */
    uploadWatermarked: async (file, token) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BASE_URL}/api/files/upload?watermark=true`, {
            method: 'POST',
            // No Content-Type — the browser sets it with the multipart boundary.
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Image upload failed (${response.status})`);
        }

        const { key } = await response.json();
        return { key, url: `${S3_BASE_URL}${key}` };
    },
};

/**
 * Price shown in marketing copy (hero, FAQ, About, app banner).
 *
 * The server is the authority on what is actually charged — see
 * `subscription.price-inr` — and any screen that can reach the API should prefer
 * `subscription.priceInr` from /subscription/status. This constant exists only so
 * the static pages, which render before any auth context, quote one number that
 * can be corrected in one place.
 */
export const PRIME_PRICE_INR = 99;
export const PRIME_DURATION_DAYS = 30;

// === SUBSCRIPTION DOMAIN (FlatLoop Prime) ===
// The server owns the price and the entitlement; these calls only carry ids.
export const subscriptionApi = {
    getStatus: (token) => ApiClient.get('/subscription/status', null, token),
    createOrder: (token) => ApiClient.post('/subscription/order', {}, token),
    verify: (payload, token) => ApiClient.post('/subscription/verify', payload, token),
};

// === RENT CONTRIBUTION DOMAIN ===
export const rentApi = {
    contribute: (data, token) => ApiClient.post('/rent/contribute', data, token),
    getInsights: (params) => ApiClient.get('/public/rent/insights', params),
    getMapData: (params) => ApiClient.get('/public/rent/map-data', params),
};
